import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { BatteryCharging, LogOut, ChevronDown } from "lucide-react";
import api from "@/lib/apiClient";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { navForRole, ROLE_LABELS } from "@/lib/constants";
import NotificationBell from "@/components/NotificationBell";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const nav = navForRole(user?.role || "customer");
  const [customLinks, setCustomLinks] = useState([]);

  useEffect(() => {
    api.get("/settings/links").then((r) => setCustomLinks(r.data.custom_links || [])).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-[280px] shrink-0 flex-col fixed h-screen text-white" style={{ background: "var(--grad-side)" }}>
        <div className="h-16 flex items-center gap-3 px-6 border-b border-white/10">
          <div className="h-10 w-10 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center ring-1 ring-white/20">
            <BatteryCharging className="h-5 w-5" style={{ color: "var(--c-lime)" }} />
          </div>
          <div>
            <p className="font-display font-bold text-lg leading-none tracking-tight">Greenrgy</p>
            <p className="text-[9px] uppercase tracking-[0.18em] text-white/50 mt-1" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>Warranty Console</p>
          </div>
        </div>
        <p className="px-6 pt-5 pb-2 text-[10px] uppercase tracking-[0.18em] text-white/40" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>Menu</p>
        <nav className="flex-1 overflow-y-auto px-4 space-y-1">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/claims"}
              data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? "text-[#07361F] font-semibold"
                    : "text-white/75 hover:bg-white/10 hover:text-white"
                }`
              }
              style={({ isActive }) => (isActive ? { background: "var(--c-lime)" } : undefined)}
            >
              <item.icon className="h-[18px] w-[18px]" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="rounded-lg bg-white/10 px-3 py-2.5 text-xs">
            <p className="font-semibold text-white">{ROLE_LABELS[user?.role]}</p>
            <p className="text-white/60 truncate mt-0.5">{user?.email}</p>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 lg:ml-[280px] min-w-0 flex flex-col">
        <div className="h-1 w-full" style={{ background: "var(--grad-primary)" }} />
        <header className="h-16 border-b border-border bg-card/85 backdrop-blur-sm sticky top-0 z-30 flex items-center justify-between px-4 sm:px-8">
          <div className="flex items-center gap-2 lg:hidden">
            <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: "var(--grad-side)" }}>
              <BatteryCharging className="h-5 w-5" style={{ color: "var(--c-lime)" }} />
            </div>
            <span className="font-display font-bold">Greenrgy</span>
          </div>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-2">
            {customLinks.length > 0 && (
              <div className="hidden sm:flex items-center gap-2" data-testid="header-custom-links">
                {customLinks.map((l, i) => (
                  <a
                    key={i}
                    href={l.url}
                    data-testid={`header-custom-link-${i}`}
                    className="rounded-full border border-primary/30 bg-primary/10 px-3.5 py-1.5 text-xs font-semibold text-primary hover:bg-primary/20 transition-colors"
                  >
                    {l.label}
                  </a>
                ))}
              </div>
            )}
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-accent transition-colors" data-testid="user-menu-trigger">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs font-bold">
                      {user?.name?.slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:block text-sm font-medium max-w-[120px] truncate">{user?.name}</span>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <p className="font-semibold">{user?.name}</p>
                  <p className="text-xs font-normal text-muted-foreground">{ROLE_LABELS[user?.role]}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-red-600 focus:text-red-600" data-testid="logout-button">
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <motion.main
          key={window.location.pathname}
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
          className="flex-1 p-4 sm:p-8 max-w-[1400px] w-full"
        >
          {children}
        </motion.main>
      </div>
    </div>
  );
}
